import React, { useCallback, useState } from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import {
  Button, Icon as UiIcon,
} from '@picsio/ui';
import { CheckIcon, Warning } from '@picsio/icons';

import localization from '../../../shared/strings';
import Checkbox from '../../../UIComponents/checkbox';
import Icon from '../../Icon';
import {
  CHANNEL_ID_QUERY_PARAM,
  CHANNEL_SUB_VIEW_MAPPING_FIELDS,
  CHANNEL_SUB_VIEW_QUERY_PARAM,
} from '../../teammates/views/Channels/constants';
import styles from './styles.module.scss';

export const buildChannelMappingUrl = (salesChannelId) => (
  `/teammates?tab=channels&${CHANNEL_SUB_VIEW_QUERY_PARAM}=${CHANNEL_SUB_VIEW_MAPPING_FIELDS}&${CHANNEL_ID_QUERY_PARAM}=${salesChannelId}`
);

export const openChannelMappingInNewTab = (salesChannelId) => {
  window.open(buildChannelMappingUrl(salesChannelId), '_blank', 'noopener,noreferrer');
};

const PublishUnmappedFieldsDialog = ({
  channelsWithUnmapped,
  fullyMappedCount,
  totalChannelCount,
  onProceed,
  onCancel,
  root,
}) => {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.STAGE_LAYER.PUBLISH_CHANGES;
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const isSingleChannel = channelsWithUnmapped.length === 1 && totalChannelCount === 1;

  const title = isSingleChannel
    ? strings.TITLE_SINGLE
    : strings.TITLE_MULTI(channelsWithUnmapped.length, totalChannelCount);

  const destroy = useCallback(() => {
    try {
      root.unmount();
    } catch {
      // ignore
    }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handleProceed = useCallback(() => {
    destroy();
    onProceed?.(dontShowAgain);
  }, [destroy, dontShowAgain, onProceed]);

  const handleFixMapping = useCallback((salesChannelId) => {
    openChannelMappingInNewTab(salesChannelId);
  }, []);

  return (
    <div className={`simpleDialog ${styles.dialog}`}>
      <div className="simpleDialogUnderlayer" />
      <div className={`simpleDialogBox ${styles.dialogBox}`}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{title}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <p className={styles.description}>{strings.DESCRIPTION}</p>
            <div className={styles.blocks}>
              <If condition={fullyMappedCount > 0}>
                <div className={`${styles.block} ${styles.blockSuccess}`}>
                  <UiIcon size="xl" color="success">
                    <CheckIcon className={styles.successIcon} />
                  </UiIcon>
                  <p className={styles.blockText}>
                    {strings.FULLY_MAPPED_SUMMARY(fullyMappedCount)}
                  </p>
                </div>
              </If>
              {channelsWithUnmapped.map(({
                channelLabel,
                unmappedFieldCount,
                salesChannelId,
              }) => (
                <div
                  key={salesChannelId}
                  className={`${styles.block} ${styles.blockWarning}`}
                >
                  <UiIcon size="xl" color="warning">
                    <Warning className={styles.warningIcon} />
                  </UiIcon>
                  <p className={styles.blockText}>
                    <span className={styles.blockChannelName}>{channelLabel}: </span>
                    {strings.UNMAPPED_FIELDS_COUNT(unmappedFieldCount)}
                  </p>
                  <button
                    type="button"
                    className={styles.fixMapping}
                    onClick={() => handleFixMapping(salesChannelId)}
                  >
                    <span aria-hidden="true">{strings.FIX_MAPPING_ARROW}</span>
                    {strings.FIX_MAPPING}
                  </button>
                </div>
              ))}
            </div>
            <div className={styles.checkboxRow}>
              <Checkbox
                label={strings.DONT_SHOW_AGAIN}
                value={dontShowAgain}
                onChange={setDontShowAgain}
              />
            </div>
          </div>
        </div>
        <div className={`simpleDialogFooter ${styles.footer}`}>
          <Button color="secondary" size="md" variant="contained" onClick={handleCancel}>
            {strings.BTN_CANCEL}
          </Button>
          <Button color="primary" size="md" variant="contained" onClick={handleProceed}>
            {strings.BTN_PROCEED}
          </Button>
        </div>
      </div>
    </div>
  );
};

PublishUnmappedFieldsDialog.propTypes = {
  channelsWithUnmapped: PropTypes.arrayOf(PropTypes.shape({
    channelLabel: PropTypes.string.isRequired,
    unmappedFieldCount: PropTypes.number.isRequired,
    salesChannelId: PropTypes.string.isRequired,
  })).isRequired,
  fullyMappedCount: PropTypes.number.isRequired,
  totalChannelCount: PropTypes.number.isRequired,
  root: PropTypes.shape({
    unmount: PropTypes.func.isRequired,
  }).isRequired,
  onProceed: PropTypes.func,
  onCancel: PropTypes.func,
};

PublishUnmappedFieldsDialog.defaultProps = {
  onProceed: undefined,
  onCancel: undefined,
};

export function showPublishUnmappedFieldsDialogComponent({
  channelsWithUnmapped,
  fullyMappedCount,
  totalChannelCount,
}) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);

  return new Promise((resolve, reject) => {
    root.render(
      <PublishUnmappedFieldsDialog
        channelsWithUnmapped={channelsWithUnmapped}
        fullyMappedCount={fullyMappedCount}
        totalChannelCount={totalChannelCount}
        root={root}
        onProceed={(dontShowAgain) => resolve(dontShowAgain)}
        onCancel={() => reject(new Error('cancel'))}
      />,
    );
  });
}

export default PublishUnmappedFieldsDialog;
